import React, { isValidElement } from 'react';

import { useAuthorization } from '@hooks/useAuthorization';
import { PermissionAction, PermissionAsset } from '@redux/types/types';

import styles from './authorized.module.scss';

interface AuthorizedProps {
  /**
   * The permissions required to render the children.
   */
  requiredPermissions: Partial<Record<PermissionAsset, PermissionAction>>;
  /**
   * A fallback component to render if the user lacks permissions.
   * @default null
   */
  fallback?: React.ReactNode;
  /**
   * A fallback component to render while permissions are loading.
   * @default null
   */
  loadingFallback?: React.ReactNode;
  /**
   * If true, renders the children in a disabled state if unauthorized,
   * instead of hiding them. This is useful for maintaining UI layout consistency.
   * The child component must accept a `disabled` prop.
   * @default false
   */
  renderAsDisabled?: boolean;
}

/**
 * A wrapper component that conditionally renders its children based on user permissions.
 * It handles loading states and provides flexible fallbacks for unauthorized access.
 */
const Authorized = ({
  requiredPermissions,
  children,
  fallback = null,
  loadingFallback = null,
  renderAsDisabled = false,
}: React.PropsWithChildren<AuthorizedProps>): React.ReactNode => {
  const { hasPermission, isLoading } = useAuthorization();
  const isAuthorized = hasPermission(requiredPermissions);

  if (isLoading) return loadingFallback;

  if (isAuthorized) return children;

  if (renderAsDisabled) {
    // We need to clone the child to pass the disabled prop.
    // This requires the child to be a single React element that can accept a 'disabled' prop.
    if (isValidElement(children)) {
      const disabledChild = React.cloneElement(children as React.ReactElement<any>, {
        disabled: true,
      });

      return (
        <div className={styles.disabledWrapper} data-testid="disabled-wrapper">
          {disabledChild}
        </div>
      );
    }

    // Fallback if children are not a single valid element, though this case should be avoided.
    return fallback;
  }

  return fallback;
};

export default Authorized;
